import { sampleQueryKeys } from '@/config/queryKeys';
import { useDateFormatter, useScrollIntoViewOnData } from '@/hooks';
import { useApiClient } from '@/hooks/useApiClient';
import type {
  PaginatedSampleProteoformsResponse,
  SampleProteoformPublic,
  SampleProteoformSearchRequest,
  SampleWithNamesPublic,
} from '@/types';
import { FullLoading } from '@components/Loaders';
import { Table } from '@nautilusbio/nb-design-system/Table';
import { useQuery } from '@tanstack/react-query';
import type { ColDef, GridApi, IRowNode } from 'ag-grid-enterprise';
import React, { FC, memo, useEffect, useMemo, useRef } from 'react';
import { useTranslation } from 'react-i18next';

import { showToast } from '../helpers';
import styles from '../styles.module.css';

interface ProteoformDetailRendererProps {
  data: SampleWithNamesPublic;
  orgId: string;
  node: IRowNode<SampleWithNamesPublic>;
  api?: GridApi<SampleWithNamesPublic>;
}

const ProteoformDetailRendererComponent: FC<ProteoformDetailRendererProps> = ({ data, orgId, node, api: gridApi }) => {
  const { t } = useTranslation(['studies', 'general']);
  const parentNode = node.parent;
  const api = useApiClient();
  const formatDate = useDateFormatter();
  const hasHandled = useRef(false);
  const detailPanelRef = useRef<HTMLDivElement>(null);

  const {
    data: detailData,
    isLoading,
    isError,
  } = useQuery({
    queryKey: sampleQueryKeys.proteoformsSearch(orgId, data.id),
    queryFn: async () => {
      const url = sampleQueryKeys.endpoints.proteoformsSearch(orgId, data.id);

      const req: SampleProteoformSearchRequest = {
        skip: 0,
        limit: 100,
        sort_by: 'proteoform_name',
        sort_order: 'desc',
      };
      const response = await api.post<PaginatedSampleProteoformsResponse>(url, req);

      if (!response) throw new Error('No response from server');

      return response;
    },
    enabled: !!orgId && !!data.id,
    retry: 1,
  });

  const hasData = !!detailData && detailData.records.length > 0;
  useScrollIntoViewOnData(detailPanelRef, isLoading, hasData, isError);

  const detailColumns: ColDef<SampleProteoformPublic>[] = useMemo(
    () => [
      { headerName: t('studies:columnHeaders.proteoform'), field: 'proteoform_name', flex: 1 },
      {
        headerName: t('studies:columnHeaders.abundance'),
        field: 'proteoform_abundance',
        valueFormatter: ({ value }: { value: number }) =>
          value !== undefined && value !== null ? value.toLocaleString() : '',
      },
      { headerName: t('studies:columnHeaders.wellPosition'), field: 'well_position' },
      {
        headerName: t('studies:columnHeaders.createdAt'),
        field: 'created_at',
        valueFormatter: ({ value }) => formatDate(value),
      },
    ],
    [formatDate, t],
  );

  useEffect(() => {
    if (!isLoading && parentNode && !hasHandled.current) {
      if (isError) {
        hasHandled.current = true;
        parentNode.setExpanded(false);
        showToast(t('studies:proteoforms.error'));
      } else if (detailData && detailData.records.length === 0) {
        hasHandled.current = true;
        parentNode.setExpanded(false);
        showToast(t('studies:proteoforms.noData'));
      } else if (detailData && detailData.records.length > 0) {
        hasHandled.current = true;
        requestAnimationFrame(() => {
          if (gridApi && parentNode) {
            gridApi.ensureNodeVisible(parentNode, 'middle');
          }
        });
      }
    }
  }, [isLoading, isError, detailData, parentNode, node, data.id, gridApi, t]);

  if (isLoading) {
    return <FullLoading fullHeight fullWidth label={t('studies:proteoforms.loading')} ref={detailPanelRef} />;
  }

  if (isError) {
    // Ensure parent row collapses on error
    if (parentNode && parentNode.expanded && !hasHandled.current) {
      parentNode.setExpanded(false);
    }
    return null;
  }
  if (!detailData?.records?.length) {
    // Ensure parent row collapses when no data
    if (parentNode && parentNode.expanded && !hasHandled.current) {
      parentNode.setExpanded(false);
    }
    return null;
  }

  return (
    <Table<SampleProteoformPublic>
      ariaLabel={t('studies:proteoforms.title')}
      columnDefs={detailColumns}
      container={{
        className: styles.tableContainer,
        ref: detailPanelRef,
      }}
      domLayout="autoHeight"
      enableResponsiveResize
      pagination={detailData.total > detailData.limit}
      paginationPageSize={detailData.limit}
      rowData={detailData.records}
      tableId={`proteoforms-table-${data.id}`}
      zebraStriping
    />
  );
};

export const ProteoformDetailRenderer = memo(ProteoformDetailRendererComponent);

export default ProteoformDetailRenderer;
